import React from "react";

import {
  ComboBox,
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import { makeEvent } from "web_modules/shared/FormHelpers/carbon-helpers";

interface Props {
  fieldDef: any;
  value: any;
  invalidMsg: string;
  handleChange: Function;
}

const Field: React.FC<Props> = (props: Props) => {
  // vars
  const { fieldDef, value, invalidMsg, handleChange } = props;

  // fcns
  const handleComboBoxChange = (name, data, defaultValue) => {
    const val = data && data.selectedItem ? data.selectedItem.value : defaultValue;

    return handleChange(name, val);
  };

  const handleInputChange = event => {
    const { target } = event;
    const { name } = target;
    let { value: val } = target;

    if (fieldDef.beforeHandle) {
      val = fieldDef.beforeHandle(val);
    }

    return handleChange(name, val);
  };

  const translateValue = v => {
    if (v === null || v === undefined) {
      return "";
    }
    if (typeof v === "boolean" && v === true) {
      return "Yes";
    }
    if (typeof v === "boolean" && v === false) {
      return "No";
    }
    return v;
  };

  // return
  switch (fieldDef.type) {
    case "text":
      return (
        <TextInput
          key={fieldDef.name}
          id={fieldDef.name}
          name={fieldDef.name}
          disabled={fieldDef.disabled}
          value={translateValue(value)}
          labelText={fieldDef.label}
          placeholder={fieldDef.placeholder}
          type="text"
          invalid={invalidMsg !== undefined}
          invalidText={invalidMsg}
          onChange={handleInputChange}
          size="sm"
        />
      );
    case "select":
      return (
        <Select
          key={fieldDef.name}
          id={fieldDef.name}
          name={fieldDef.name}
          value={translateValue(value)}
          invalid={invalidMsg !== undefined}
          invalidText={invalidMsg}
          labelText={fieldDef.label}
          onChange={handleInputChange}
          size="sm"
        >
          {fieldDef.options.map(opt => (
            <SelectItem
              key={opt}
              text={fieldDef.formatForDisplay ? fieldDef.formatForDisplay(opt) : opt}
              value={opt}
            />
          ))}
        </Select>
      );
    case "date":
      return (
        <DatePicker
          id="date-picker-start-date-single"
          datePickerType="single"
          dateFormat="Y-m-d"
          style={{ marginBottom: "10px" }}
          onChange={e => {
            handleInputChange(makeEvent(fieldDef.name, e[0]));
          }}
        >
          <DatePickerInput
            id="date-picker-start-date-single-input"
            name="start_date_single"
            iconDescription={`Select a date value for ${fieldDef.name}`}
            labelText="Start Date *"
            placeholder="yyyy-mm-dd"
            type="text"
            size="sm"
            disabled={fieldDef.disabled || false}
          />
        </DatePicker>
      );
    case "combobox":
      const options = fieldDef.options.map(opt => ({
        key: opt,
        text: opt,
        value: opt,
      }));

      const defaultOption = options[0];

      return (
        <ComboBox
          titleText={fieldDef.label}
          id={fieldDef.name}
          name={fieldDef.name}
          key={fieldDef.name}
          placeholder={defaultOption.value}
          light
          size="sm"
          invalid={invalidMsg !== undefined}
          invalidText={invalidMsg}
          items={options}
          itemToString={e => (e ? e.text : "")}
          initialSelectedItem={options.find(opt => opt.value === value)}
          onChange={e => handleComboBoxChange(fieldDef.name, e, defaultOption.value)}
        />
      );
    default:
      throw new Error(`field type '${fieldDef.type}' not supported`);
  }
};

export default Field;
